import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APPS, type AppDefinition } from "@acruxsoft/platform-shared";
import { useAuthCtx } from "../lib/auth-provider";
import { mintRedirectToken } from "../lib/callables";
import { userLabel } from "../lib/user-label";
import { appAccess, licenseEndsAt } from "../lib/app-access";
import { useMyLicenses } from "../hooks/use-my-licenses";
import { Spinner } from "../components/spinner";
import { AppTile } from "../components/app-tile";
import { LicenseRequestDialog } from "../components/license-request-dialog";

export function HomePage() {
  const nav = useNavigate();
  const { user, claims, loading } = useAuthCtx();
  const { t } = useTranslation();
  // Keyed by appId so two tiles can't share one spinner or one error.
  const [launchingAppId, setLaunchingAppId] = useState<string | null>(null);
  const [launchErrors, setLaunchErrors] = useState<Record<string, string>>({});
  const [requesting, setRequesting] = useState<AppDefinition | null>(null);
  const [sentFor, setSentFor] = useState<string | null>(null);
  const licenses = useMyLicenses();

  useEffect(() => {
    if (!loading && !user) nav("/login?return=/home", { replace: true });
  }, [user, loading]);

  if (loading || (user && !claims) || licenses.loading) return <Spinner />;
  if (!user) return null;

  const isAdmin = (claims as { admin?: boolean } | null)?.admin === true;
  const entitlements = claims?.entitlements ?? [];
  const now = Date.now();

  // Email verification is the prerequisite for any access, free apps included:
  // mintRedirectToken refuses unverified users, and it's the only thing that
  // keeps bot signups from walking into TVM.
  const emailVerified = user.emailVerified;

  async function openApp(app: AppDefinition) {
    if (!user) return;
    setLaunchingAppId(app.appId);
    setLaunchErrors((prev) => {
      const { [app.appId]: _dropped, ...rest } = prev;
      return rest;
    });
    try {
      await user.getIdToken(true);
      const { data } = await mintRedirectToken({ audience: app.launchHost });
      window.location.href = `https://${app.launchHost}/auth/callback?token=${encodeURIComponent(data.customToken)}`;
    } catch (e: unknown) {
      console.error("[home] mintRedirectToken failed", { appId: app.appId, error: e });
      setLaunchErrors((prev) => ({
        ...prev,
        [app.appId]: t("home.appTile.launchError", { app: t(`home.apps.${app.appId}.shortName`) })
      }));
      setLaunchingAppId(null);
    }
  }

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }} className="acx__stack acx__stack--lg">
      <header className="acx__home-hero">
        <span className="acx__card-eyebrow">{t("home.eyebrow")}</span>
        <h1 className="acx__home-title">{t("home.welcome", { email: userLabel(user) })}</h1>
      </header>

      {!emailVerified && (
        <div className="acx__banner acx__banner--info" role="status">
          <strong>{t("home.verifyBanner.title")}</strong>
          <span>{t("home.verifyBanner.body")}</span>
        </div>
      )}

      {sentFor && (
        <div className="acx__banner acx__banner--success" role="status">
          <strong>{t("home.request.sentTitle")}</strong>
          <span>{t("home.request.sentBody", { app: t(`home.apps.${sentFor}.name`) })}</span>
        </div>
      )}

      <section>
        <h2 className="acx__section-title">{t("home.appsHeader")}</h2>
        <div className="acx__apps-grid">
          {APPS.map((app) => {
            const grant = licenses.grantFor(app.appId);
            const request = licenses.requestFor(app.appId);
            const access = appAccess({ app, emailVerified, entitlements, grant, request, now });
            return (
              <AppTile
                key={app.appId}
                app={app}
                access={access}
                licenseEndsAt={licenseEndsAt(grant, now)}
                requestedAt={request?.updatedAt ?? null}
                expiredAt={grant?.expiresAt ?? null}
                launching={launchingAppId === app.appId}
                error={launchErrors[app.appId] ?? null}
                onOpen={openApp}
                onRequest={(a) => {
                  setSentFor(null);
                  setRequesting(a);
                }}
              />
            );
          })}

          {isAdmin && (
            <article className="acx__app-tile acx__app-tile--admin">
              <div className="acx__app-tile-row">
                <span
                  className="acx__brand-mark acx__brand-mark--admin"
                  style={{ width: 52, height: 52 }}
                  aria-hidden
                >
                  {/* Four-pointed star — visually rhymes with Acrux (the brand's namesake star). */}
                  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4 L18.2 13.8 L28 16 L18.2 18.2 L16 28 L13.8 18.2 L4 16 L13.8 13.8 Z" fill="currentColor" />
                  </svg>
                </span>
                <div className="acx__app-tile-head">
                  <span className="acx__card-eyebrow">{t("home.admin.eyebrow")}</span>
                  <h3 className="acx__app-tile-title">{t("home.admin.title")}</h3>
                </div>
              </div>
              <p className="acx__app-tile-desc">{t("home.admin.description")}</p>
              <div className="acx__app-tile-cta">
                <Link to="/admin" className="acx__btn acx__btn--primary">{t("home.admin.open")}</Link>
              </div>
            </article>
          )}
        </div>
      </section>

      {requesting && (
        <LicenseRequestDialog
          app={requesting}
          initialCompany={licenses.requestFor(requesting.appId)?.company ?? ""}
          initialMessage={licenses.requestFor(requesting.appId)?.message ?? ""}
          onClose={() => setRequesting(null)}
          onSent={() => {
            setSentFor(requesting.appId);
            setRequesting(null);
          }}
        />
      )}
    </div>
  );
}
